import { PreviousProcedures, RequestedProcedures } from '@/config/customsProcedure'
import {
  Alert,
  Button,
  Label,
  ListboxGroup,
  NumericInputGroup,
  PageSection
} from '@/common'
import { ImportedGoodFormData } from './ImportedGoodForm.schema'
import useI18n from '@/shared/hooks/usei18n'
import { useEffect, useState } from 'react'
import AddInwardProcessingInformationV2 from '../AddInwardProcessingInformationV2'
import { TrashIcon, PlusIcon } from '@/common/components/icons'

export default function ImportedGoodCustomsStatusForm({
  data,
  onChange
}: {
  data: ImportedGoodFormData
  onChange: (data: ImportedGoodFormData) => void
}) {
  const { t } = useI18n()
  const [isInwardProcessing, setIsInwardProcessing] = useState(false)
  const [isAddingInwardProcessingInformation, setIsAddingInwardProcessingInformation] =
    useState(false)

  useEffect(() => {
    setIsInwardProcessing(
      data.previousCustomsProcedure === '51' || data.previousCustomsProcedure === '54'
    )
  }, [data.previousCustomsProcedure])

  const onPreviousProcedureChange = (previousCustomsProcedure: string) => {
    setIsInwardProcessing(previousCustomsProcedure === '51' || previousCustomsProcedure === '54')
    onChange({
      ...data,
      previousCustomsProcedure,
      inwardProcessingInformation: undefined,
      inwardProcessedNetMassInTonnes: undefined
    })
  }

  const onRequestedProcedureChange = (requestedCustomsProcedure: string) => {
    onChange({
      ...data,
      requestedCustomsProcedure
    })
  }

  const onAddInwardProcessingInformation = (payload: {
    memberState: string
    inwardProcessingWaiver: boolean
    authorisation: string
    globalisationTimeStart: string
    globalisationTimeEnd: string
    billOfDischargeDeadline: string
  }) => {
    onChange({
      ...data,
      inwardProcessingInformation: [...(data.inwardProcessingInformation || []), payload]
    })
  }

  const isInwardProcessingExceeding =
    Number(data.inwardProcessedNetMassInTonnes) > Number(data.netMassInTonnes)

  const removeInwardProcessingInformation = (index: number) => {
    onChange({
      ...data,
      inwardProcessingInformation: data.inwardProcessingInformation?.filter((_, i) => i !== index)
    })
  }

  return (
    <PageSection
      title={t('cbamReporting:cbamReportDetails.importedGoodCreateDialog.sections.customStatus')}
    >
      {isAddingInwardProcessingInformation ? (
        <AddInwardProcessingInformationV2
          back={() => setIsAddingInwardProcessingInformation(false)}
          add={onAddInwardProcessingInformation}
        />
      ) : (
        <>
          <div className="grid sm:grid-cols-2 gap-4">
            <ListboxGroup
              label={t(
                'cbamReporting:cbamReportDetails.importedGoodCreateDialog.fields.previousProcedure'
              )}
              value={data.previousCustomsProcedure ?? ''}
              onChange={value => onPreviousProcedureChange(value)}
              options={PreviousProcedures.map(procedure => ({
                label: t(`common:customsProcedures.${procedure.value}`),
                value: procedure.code
              }))}
            />
            <ListboxGroup
              label={t(
                'cbamReporting:cbamReportDetails.importedGoodCreateDialog.fields.requestedProcedure'
              )}
              value={data.requestedCustomsProcedure ?? ''}
              onChange={value => onRequestedProcedureChange(value)}
              options={RequestedProcedures.map(procedure => ({
                label: t(`common:customsProcedures.${procedure.value}`),
                value: procedure.code
              }))}
            />
          </div>
          {isInwardProcessing && (
            <div className="space-y-2">
              {isInwardProcessingExceeding && (
                <Alert type="error">
                  {t(
                    'cbamReporting:cbamReportDetails.importedGoodCreateDialog.alerts.inwardProcessingExceedingError'
                  )}
                </Alert>
              )}
              <div className="flex items-end gap-4">
                <div className="w-1/2">
                  <NumericInputGroup
                    label={t(
                      'cbamReporting:cbamReportDetails.importedGoodCreateDialog.fields.inwardProcessedNetMassInTonnes'
                    )}
                    suffix="t"
                    value={data.inwardProcessedNetMassInTonnes?.toString() ?? '0'}
                    onChange={evt =>
                      onChange({
                        ...data,
                        inwardProcessedNetMassInTonnes: Number(evt.target.value)
                      })
                    }
                  />
                </div>
                <div className="grow">
                  <Button
                    sizeType="sm"
                    icon={<PlusIcon />}
                    onClick={() => setIsAddingInwardProcessingInformation(true)}
                  >
                    {t(
                      'cbamReporting:cbamReportDetails.importedGoodCreateDialog.actions.addInwardProcessingInformation'
                    )}
                  </Button>
                </div>
              </div>
              {data.inwardProcessingInformation && data.inwardProcessingInformation.length > 0 && (
                <>
                  <Label>
                    {t(
                      'cbamReporting:cbamReportDetails.importedGoodCreateDialog.inwardProcessingInformation.title'
                    )}
                  </Label>
                  <div className="flex flex-row flex-wrap gap-2">
                    {data.inwardProcessingInformation.map((info, index) => (
                      <div key={info.memberState} className="w-fit">
                        <div className="flex justify-between gap-2 text-sm text-blue-500 overflow-hidden text-ellipsis bg-blue-300 rounded-md py-2.5 px-4">
                          {t(`countriesByIsoAlpha2.${info.memberState.toLowerCase()}`)}
                          <button
                            className="size-4 cursor-pointer"
                            onClick={() => removeInwardProcessingInformation(index)}
                          >
                            <TrashIcon className="size-6" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}
    </PageSection>
  )
}
